import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';

// Define the stats interface
interface ConnectionStats {
  resolution: string;
  fps: number;
  currentRoundTripTime: number;
  availableOutgoingBitrate: number;
  packetsReceived: number;
  packetsLost: number;
}

// Simulated WebRTC Enterprise Demo (WSL-compatible)
const WebRTCEnterpriseDemo: React.FC = () => {
  const [connectionStatus, setConnectionStatus] = useState('Initializing...');
  const [isConnected, setIsConnected] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);
  const [stats, setStats] = useState<ConnectionStats>({
    resolution: '1280x720',
    fps: 30,
    currentRoundTripTime: 45,
    availableOutgoingBitrate: 2500,
    packetsReceived: 1254,
    packetsLost: 2
  });
  
  // Add log entry
  const addLog = (message: string) => {
    console.log(message);
    setLogs(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };
  
  // Simulate WebRTC connection process
  useEffect(() => {
    // Simulate connection process
    const simulateConnection = async () => {
      addLog('Starting WebRTC initialization...');
      setConnectionStatus('Checking environment...');
      await sleep(800);
      
      addLog('Environment check passed');
      setConnectionStatus('Requesting media permissions...');
      await sleep(1200);
      
      addLog('Media permissions granted (simulated)');
      setConnectionStatus('Creating peer connection...');
      await sleep(1000);
      
      addLog('Peer connection created');
      setConnectionStatus('Gathering ICE candidates...');
      await sleep(1500);
      
      addLog('ICE candidate: candidate:1 1 UDP 2122252543 192.168.1.100 49823 typ host');
      addLog('ICE candidate: candidate:2 1 TCP 1518280447 192.168.1.100 9 typ host tcptype active');
      await sleep(800);
      
      addLog('Creating SDP offer...');
      setConnectionStatus('Creating offer...');
      await sleep(1000);
      
      addLog('SDP offer created');
      setConnectionStatus('Waiting for answer...');
      await sleep(1200);
      
      addLog('SDP answer received');
      setConnectionStatus('Establishing connection...');
      await sleep(1500);
      
      addLog('Connection established');
      setConnectionStatus('Connected');
      setIsConnected(true);
      
      // Start updating stats periodically
      const statsInterval = setInterval(() => {
        setStats((prev: ConnectionStats) => ({
          resolution: prev.resolution,
          fps: Math.max(25, Math.min(60, prev.fps + Math.floor(Math.random() * 7) - 3)),
          currentRoundTripTime: Math.max(20, Math.min(150, prev.currentRoundTripTime + Math.floor(Math.random() * 11) - 5)),
          availableOutgoingBitrate: Math.max(1000, Math.min(5000, prev.availableOutgoingBitrate + Math.floor(Math.random() * 200) - 100)),
          packetsReceived: prev.packetsReceived + Math.floor(Math.random() * 50) + 10,
          packetsLost: prev.packetsLost + (Math.random() > 0.8 ? 1 : 0)
        }));
      }, 2000);
      
      return () => clearInterval(statsInterval);
    };
    
    simulateConnection();
  }, []);
  
  // Helper function for async sleep
  const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
  
  // Toggle screen sharing (simulated)
  const toggleScreenShare = async () => {
    if (!isScreenSharing) {
      addLog('Requesting screen sharing permission...');
      await sleep(1000);
      addLog('Screen sharing started');
      setIsScreenSharing(true);
      
      // Update stats to reflect screen sharing
      setStats(prev => ({
        ...prev,
        resolution: '1920x1080', // Higher resolution for screen sharing
        fps: 15 // Lower FPS is typical for screen sharing
      }));
    } else {
      addLog('Screen sharing stopped');
      setIsScreenSharing(false);
      
      // Restore original stats
      setStats(prev => ({
        ...prev,
        resolution: '1280x720',
        fps: 30
      }));
    }
  };
  
  // Send test message (simulated)
  const sendMessage = async () => {
    const message = `Test message: ${new Date().toLocaleTimeString()}`;
    addLog(`Sending message: ${message}`);
    await sleep(300);
    addLog(`Message sent successfully`);
    
    // Simulate receiving a response
    await sleep(800);
    addLog(`Received response: Message received at ${new Date().toLocaleTimeString()}`);
  };
  
  // Render different content based on screen sharing state
  const renderLocalStream = () => {
    if (isScreenSharing) {
      return (
        <div className="screen-sharing-view">
          <div className="screen-sharing-header">
            <span className="screen-title">Presentation.pptx</span>
            <span className="screen-sharing-badge">SCREEN SHARING</span>
          </div>
          <div className="screen-sharing-content">
            <h2>WebRTC Enterprise Features</h2>
            <ul>
              <li>Secure P2P Communication</li>
              <li>Low Latency Video Conferencing</li>
              <li>Real-time Data Channels</li>
              <li>Adaptive Streaming Quality</li>
            </ul>
          </div>
        </div>
      );
    } else {
      return (
        <div className="video-stream-view">
          <div className="placeholder-icon">📹</div>
          <div className="placeholder-text">Local Video Stream</div>
        </div>
      );
    }
  };
  
  return (
    <div className="webrtc-demo">
      <header>
        <h1>WebRTC Enterprise Collaboration Platform</h1>
        <div className="connection-status">
          <span className={isConnected ? 'connected' : 'disconnected'}>
            {connectionStatus}
          </span>
        </div>
      </header>
      
      <div className="main-content">
        <div className="video-container">
          <div className="video-wrapper">
            <div className={`video-placeholder ${isScreenSharing ? 'screen-sharing' : ''}`}>
              {renderLocalStream()}
            </div>
            <div className="video-label">Local Stream {isScreenSharing && '(Screen Sharing)'}</div>
          </div>
          
          <div className="video-wrapper">
            <div className="video-placeholder">
              {isScreenSharing ? (
                <div className="remote-viewing-screen">
                  <div className="remote-viewing-icon">👁️</div>
                  <div className="remote-viewing-text">Viewing your shared screen</div>
                </div>
              ) : (
                <div className="video-stream-view">
                  <div className="placeholder-icon">👥</div>
                  <div className="placeholder-text">Remote Video Stream</div>
                </div>
              )}
            </div>
            <div className="video-label">Remote Stream</div>
          </div>
        </div>
        
        <div className="controls">
          <button 
            onClick={toggleScreenShare}
            className={isScreenSharing ? 'active' : ''}
          >
            {isScreenSharing ? 'Stop Sharing' : 'Share Screen'}
          </button>
          
          <button onClick={sendMessage} disabled={!isConnected}>
            Send Test Message
          </button>
        </div>
        
        <div className="stats-panel">
          <h3>Connection Statistics</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-label">Resolution:</span>
              <span className="stat-value">{stats.resolution}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Frame Rate:</span>
              <span className="stat-value">{stats.fps} fps</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Round Trip Time:</span>
              <span className="stat-value">{stats.currentRoundTripTime} ms</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Available Bitrate:</span>
              <span className="stat-value">{stats.availableOutgoingBitrate} kbps</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Packets Received:</span>
              <span className="stat-value">{stats.packetsReceived}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Packets Lost:</span>
              <span className="stat-value">{stats.packetsLost}</span>
            </div>
          </div>
        </div>
        
        <div className="log-panel">
          <h3>Connection Log</h3>
          <div className="log-container">
            {logs.map((log, index) => (
              <div key={index} className="log-entry">{log}</div>
            ))}
          </div>
        </div>
      </div>
      
      <footer>
        <div className="tech-stack">
          <h3>Technology Stack</h3>
          <ul>
            <li>WebRTC API</li>
            <li>React with TypeScript</li>
            <li>STUN/TURN for NAT traversal</li>
            <li>Real-time data channels</li>
            <li>Adaptive bitrate streaming</li>
            <li>End-to-end encryption</li>
          </ul>
        </div>
        <div className="features">
          <h3>Enterprise Features</h3>
          <ul>
            <li>Multi-party video conferencing</li>
            <li>Screen sharing with annotation</li>
            <li>Secure file transfer</li>
            <li>Real-time collaborative editing</li>
            <li>Meeting recording & transcription</li>
            <li>Advanced network analytics</li>
          </ul>
        </div>
        <div className="architecture">
          <h3>Architecture Highlights</h3>
          <ul>
            <li>Peer-to-peer communication</li>
            <li>Signaling server for connection establishment</li>
            <li>Fallback to TURN relay if direct connection fails</li>
            <li>Scalable to hundreds of concurrent users</li>
            <li>Enterprise-grade security</li>
            <li>Cross-platform compatibility</li>
          </ul>
        </div>
      </footer>
    </div>
  );
};

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <WebRTCEnterpriseDemo />
  </React.StrictMode>
); 